/**
 * The Add a guest fields (screen 07b): first name, last name and initials.
 * Initials fill in from the name until the admin edits them, and never run
 * past two letters. Guests are saved to the pool, not to one grid.
 */
import { Input } from "@sportsmet/ui";
import type { ReactNode } from "react";

import {
  SQUARES_GUEST_FIRST_NAME,
  SQUARES_GUEST_INITIALS,
  SQUARES_GUEST_INITIALS_HINT,
  SQUARES_GUEST_LAST_NAME,
  SQUARES_GUEST_NOTE,
} from "./squaresCopy";
import { squaresCleanInitials, squaresInitialsFromName } from "./squaresModel";

export type typeSquaresGuestDraft = {
  firstName: string;
  lastName: string;
  initials: string;
  /** True once the admin typed their own initials. */
  isInitialsEdited: boolean;
};

export const EMPTY_SQUARES_GUEST_DRAFT: typeSquaresGuestDraft = {
  firstName: "",
  lastName: "",
  initials: "",
  isInitialsEdited: false,
};

export function isSquaresGuestDraftDirty(
  draft: typeSquaresGuestDraft,
): boolean {
  return (
    draft.firstName.trim() !== "" ||
    draft.lastName.trim() !== "" ||
    draft.initials.trim() !== ""
  );
}

export function isSquaresGuestDraftReady(
  draft: typeSquaresGuestDraft,
): boolean {
  return draft.firstName.trim() !== "" && draft.initials.trim() !== "";
}

function withNames(
  draft: typeSquaresGuestDraft,
  names: { firstName: string; lastName: string },
): typeSquaresGuestDraft {
  return {
    ...draft,
    ...names,
    initials: draft.isInitialsEdited
      ? draft.initials
      : squaresInitialsFromName(names),
  };
}

export const SquaresAddGuestForm = (props: {
  value: typeSquaresGuestDraft;
  onChange: (next: typeSquaresGuestDraft) => void;
}): ReactNode => {
  const { value } = props;
  return (
    <div className="flex flex-col gap-3" data-testid="squares-add-guest-form">
      <div className="grid grid-cols-2 gap-3">
        <Input
          label={SQUARES_GUEST_FIRST_NAME}
          value={value.firstName}
          autoComplete="off"
          maxLength={40}
          required
          data-testid="squares-guest-first"
          onChange={(event) => {
            props.onChange(
              withNames(value, {
                firstName: event.target.value,
                lastName: value.lastName,
              }),
            );
          }}
        />
        <Input
          label={SQUARES_GUEST_LAST_NAME}
          value={value.lastName}
          autoComplete="off"
          maxLength={40}
          data-testid="squares-guest-last"
          onChange={(event) => {
            props.onChange(
              withNames(value, {
                firstName: value.firstName,
                lastName: event.target.value,
              }),
            );
          }}
        />
      </div>
      <Input
        label={SQUARES_GUEST_INITIALS}
        hint={SQUARES_GUEST_INITIALS_HINT}
        value={value.initials}
        autoComplete="off"
        maxLength={2}
        required
        data-testid="squares-guest-initials"
        onChange={(event) => {
          props.onChange({
            ...value,
            initials: squaresCleanInitials(event.target.value),
            isInitialsEdited: true,
          });
        }}
      />
      <p className="text-caption text-ink-secondary">{SQUARES_GUEST_NOTE}</p>
    </div>
  );
};
